import i18next from "src/i18n/config";
import { createCustomColumnHelper } from "src/types/tanstack";
import { filesize } from "src/ui/utils/filesize";

import {
    getDeviceBatteryHealthDesc,
    getDeviceBatteryLevelDesc,
    getDeviceBatteryPluggedDesc,
    getDeviceBatteryStatusDesc,
    getDeviceBatteryVoltageDesc,
} from "@dashboard/shared/battery";
import { getDeviceWarnings, getIPs } from "@devices/store/helpers";
import { DeviceWarning, Group, License } from "@devices/types";
import {
    getActiveConnectionType,
    getDeviceConnectionLabel,
    getDeviceOnlineSince,
    getDeviceUptime,
    getUsableNetworkMac,
    getWifiMac,
} from "@devices/utils/device";
import {
    getLicenseActivationStatusKey,
    getLicenseActivationStatusLabel,
    getSummaryIndicatorData,
    LicenseActivationStatusKey,
} from "@devices/utils/license";
import { Theme } from "@mui/material";
import SemverService from "@services/semverService/semverService";
import TimeService from "@services/timeService/timeService";
import { StatusIndicatorCell } from "@shared/table/StatusIndicatorCell";

import { columnFilter } from "./filters/DeviceTableFilters.utils";
import {
    GroupCell,
    LastActivityCell,
    MoveLicenseCell,
    NameCell,
    TableCell,
    TableCellWithTooltip,
    TruncatedTableCell,
    WarningsCell,
} from "./Cells";

export type DeviceTableData = {
    id: number;
    chargeId: string | null;
    onlineStatus: string;
    status: { color: string; tooltip: string };
    licenseStatus: LicenseActivationStatusKey;
    name: string | null;
    comment: string | null;
    groupName: string | null;
    codename: string | null;
    serial: string | null;
    memoryTotal: number | null;
    memoryAvailable: number | null;
    batteryLevel: string | null;
    batteryVoltage: string | null;
    batteryPlugged: string | null;
    batteryStatus: string | null;
    batteryHealth: string | null;
    description: string | null;
    systemVersion: string | null;
    systemUptime: string | null;
    systemTime: string | null;
    systemTimezone: string | null;
    hardwareProduct: string | null;
    hardwareModel: string | null;
    hardwareManufacturer: string | null;
    wifiNetwork: string | null;
    onlineSince: string | null;
    wifiMac: string | null;
    ethernetMac: string | null;
    imei: string | null;
    ipv4: string | null;
    ipv6: string | null;
    connection: string | null;
    code: string;
    groupId: number;
    lastActivityDate: string | null;
    warnings: string;
    warningsList: DeviceWarning[];
    queryMap: Record<string, string>;
};
export type ColumnId =
    | "batteryHealth"
    | "batteryLevel"
    | "batteryPlugged"
    | "batteryStatus"
    | "batteryVoltage"
    | "chargeId"
    | "codename"
    | "code"
    | "comment"
    | "connection"
    | "description"
    | "groupName"
    | "imei"
    | "ipv4"
    | "ipv6"
    | "lastActivityDate"
    | "licenseStatus"
    | "memoryAvailable"
    | "memoryTotal"
    | "moveLicenseCheckbox"
    | "name"
    | "onlineSince"
    | "onlineStatus"
    | "serial"
    | "status"
    | "systemTime"
    | "systemTimezone"
    | "systemUptime"
    | "systemVersion"
    | "hardwareProduct"
    | "hardwareModel"
    | "hardwareManufacturer"
    | "wifiMac"
    | "ethernetMac"
    | "wifiNetwork"
    | "chargeId"
    | "warnings";

const getDeviceSystemTime = (row: License) => {
    const { properties } = row.device?.lastState || {};
    const timezone = properties?.["persist.sys.timezone"] || "UTC";
    const deviceMs = row.device?.lastState?.datetime?.currentMs;
    return TimeService.getWithTimeZone(deviceMs, timezone);
};

export type StableOrderState = {
    map: Map<number, number>;
    next: number;
};

export const applyStableOrder = <T extends { id: number }>(rows: T[], order: StableOrderState): T[] => {
    const { map } = order;
    for (const row of rows) {
        if (!map.has(row.id)) {
            map.set(row.id, order.next++);
        }
    }
    return [...rows].sort((a, b) => (map.get(a.id) ?? 0) - (map.get(b.id) ?? 0));
};

export const getDeviceTableData = (
    licenses: License[],
    groupsById: Record<number, Group | undefined>,
    isExpired: boolean,
    theme: Theme,
    isValid?: boolean,
) => {
    return licenses.map((item) => {
        const battery = item.device?.lastState?.battery;
        const warnings = getDeviceWarnings(item.device, groupsById[item.groupId]);
        return {
            id: item.id,
            chargeId: item.chargeId || null,
            status: getSummaryIndicatorData(item, warnings, isExpired, theme, isValid),
            onlineStatus: item.device ? (item.device.isOnline ? "Online" : "Offline") : "",
            licenseStatus: getLicenseActivationStatusKey(item),
            name: item.device?.name || null,
            comment: item.comment || null,
            groupName: item.groupName,
            codename: item.device?.product || null,
            serial: item.device?.serial || null,
            memoryTotal: item.device?.lastState?.memory?.total || null,
            memoryAvailable: item.device?.lastState?.memory?.available || null,
            batteryLevel: getDeviceBatteryLevelDesc(battery),
            batteryVoltage: getDeviceBatteryVoltageDesc(battery),
            batteryPlugged: getDeviceBatteryPluggedDesc(battery),
            batteryStatus: getDeviceBatteryStatusDesc(battery),
            batteryHealth: getDeviceBatteryHealthDesc(battery),
            description: item.device?.description || null,
            displaySize: item.device?.lastState?.display?.size || null,
            displayRotation: item.device?.lastState?.display?.rotation || null,
            displayDensity: item.device?.lastState?.properties?.["ro.sf.lcd_density"] || null,
            systemVersion: item.device?.lastState?.version?.emteriaVersion || null,
            systemUptime: getDeviceUptime(item.device),
            systemTime: getDeviceSystemTime(item),
            systemTimezone: item.device?.lastState?.properties?.["persist.sys.timezone"] || null,
            hardwareProduct: item.device?.lastState?.build?.product || null,
            hardwareModel: item.device?.lastState?.build?.model || null,
            hardwareManufacturer: item.device?.lastState?.build?.manufacturer || null,
            wifiMac: getWifiMac(item.device?.lastState),
            ethernetMac: getUsableNetworkMac(item.device?.lastState?.networks, "eth"),
            wifiNetwork:
                item.device?.lastState?.wifi?.connected && item.device?.lastState?.wifi?.ssid
                    ? item.device?.lastState?.wifi?.ssid
                    : null,
            onlineSince: getDeviceOnlineSince(item.device),
            ipv4: getIPs(item.device?.lastState || undefined).ipv4,
            ipv6: getIPs(item.device?.lastState || undefined).ipv6,
            connection: getDeviceConnectionLabel(
                getActiveConnectionType(item.device?.lastState?.networks, !!item.device?.isOnline),
            ),
            groupId: item.groupId,
            code: item.activationCode,
            imei: item.device?.lastState?.telephony?.imei || null,
            lastActivityDate: item.device?.lastActivityDate || null,
            warnings: warnings.length ? "Warning" : "OK",
            warningsList: warnings,
            queryMap: item.queryMap,
        };
    });
};

const columnHelper = createCustomColumnHelper<DeviceTableData, ColumnId>();
export const tableColumns = [
    columnHelper.accessor("id", {
        id: "moveLicenseCheckbox",
        header: "",
        enableSorting: false,
        enableHiding: false,
        cell: (info) => <MoveLicenseCell id="moveLicenseCheckbox" value={info.getValue()} />,
    }),
    columnHelper.group({
        header: i18next.t("pages:devicePage.table.groupHeader.status"),
        columns: [
            columnHelper.accessor("status", {
                id: "status",
                header: "",
                enableSorting: false,
                cell: (info) => {
                    const value = info.getValue<{ color: string; tooltip: string }>();
                    return <StatusIndicatorCell color={value.color} label={value.tooltip} />;
                },
            }),
            columnHelper.accessor("licenseStatus", {
                id: "licenseStatus",
                header: i18next.t("pages:devicePage.table.header.license"),
                meta: { filterVariant: "license" },
                filterFn: columnFilter,
                cell: (info) => {
                    const status = info.row.original.licenseStatus;
                    return <TableCell value={getLicenseActivationStatusLabel(status)} />;
                },
            }),
            columnHelper.accessor("onlineStatus", {
                id: "onlineStatus",
                header: i18next.t("common:status.online"),
                meta: { filterVariant: "deviceOnline" },
                filterFn: columnFilter,
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("warnings", {
                id: "warnings",
                header: i18next.t("pages:devicePage.table.header.health"),
                filterFn: columnFilter,
                cell: (info) => {
                    const value = info.row.original.warnings;
                    const warningsList = info.row.original.warningsList;
                    return <WarningsCell value={value} warningsList={warningsList} />;
                },
            }),
        ],
    }),
    columnHelper.group({
        header: i18next.t("common:label.general"),
        columns: [
            columnHelper.accessor("code", {
                id: "code",
                header: i18next.t("pages:devicePage.table.header.code"),
                sortingFn: "text",
                cell: (info) => <TableCellWithTooltip value={info.getValue()} />,
            }),
            columnHelper.accessor("chargeId", {
                id: "chargeId",
                header: i18next.t("pages:devicePage.table.header.chargeId"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("name", {
                id: "name",
                header: i18next.t("pages:devicePage.table.header.name"),
                cell: (info) => <NameCell value={info.getValue()} />,
            }),
            columnHelper.accessor("comment", {
                id: "comment",
                header: i18next.t("common:label.comment"),
                cell: (info) => <TruncatedTableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("description", {
                id: "description",
                header: i18next.t("common:label.description"),
                cell: (info) => <TruncatedTableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("groupName", {
                id: "groupName",
                header: i18next.t("pages:devicePage.table.header.group"),
                meta: { filterVariant: "groupName" },
                cell: (info) => <GroupCell id={info.row.original.groupId} value={info.getValue()} />,
                filterFn: (row, _columnId, filterValue: string[]) => {
                    return filterValue.includes(String(row.original.groupId));
                },
            }),
        ],
    }),
    columnHelper.group({
        header: i18next.t("pages:devicePage.table.groupHeader.product"),
        columns: [
            columnHelper.accessor("codename", {
                id: "codename",
                header: i18next.t("pages:devicePage.table.header.codename"),
                meta: { filterVariant: "codename" },
                filterFn: columnFilter,
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("hardwareProduct", {
                id: "hardwareProduct",
                header: i18next.t("pages:devicePage.table.header.product"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("hardwareModel", {
                id: "hardwareModel",
                header: i18next.t("pages:devicePage.table.header.model"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("hardwareManufacturer", {
                id: "hardwareManufacturer",
                header: i18next.t("pages:devicePage.table.header.manufacturer"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
        ],
    }),
    columnHelper.group({
        header: i18next.t("pages:devicePage.table.groupHeader.hardware"),
        columns: [
            columnHelper.accessor("serial", {
                id: "serial",
                header: i18next.t("pages:devicePage.table.header.serial"),
                sortingFn: "text",
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("imei", {
                id: "imei",
                header: i18next.t("pages:devicePage.table.header.imei"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("wifiMac", {
                id: "wifiMac",
                header: i18next.t("pages:devicePage.table.header.wifiMac"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("ethernetMac", {
                id: "ethernetMac",
                header: i18next.t("pages:devicePage.table.header.ethernetMac"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
        ],
    }),
    columnHelper.group({
        header: i18next.t("pages:devicePage.table.groupHeader.memory"),
        columns: [
            columnHelper.accessor("memoryTotal", {
                id: "memoryTotal",
                header: i18next.t("pages:devicePage.table.header.total"),
                cell: (info) => <TableCell value={filesize(info.getValue<number>())} />,
            }),
            columnHelper.accessor("memoryAvailable", {
                id: "memoryAvailable",
                header: i18next.t("pages:devicePage.table.header.available"),
                cell: (info) => <TableCell value={filesize(info.getValue<number>())} />,
            }),
        ],
    }),
    columnHelper.group({
        header: i18next.t("pages:devicePage.table.groupHeader.battery"),
        columns: [
            columnHelper.accessor("batteryLevel", {
                id: "batteryLevel",
                header: i18next.t("pages:devicePage.table.header.level"),
                sortingFn: "alphanumeric",
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("batteryVoltage", {
                id: "batteryVoltage",
                header: i18next.t("pages:devicePage.table.header.voltage"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("batteryStatus", {
                id: "batteryStatus",
                header: i18next.t("pages:devicePage.table.header.status"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("batteryHealth", {
                id: "batteryHealth",
                header: i18next.t("pages:devicePage.table.header.health"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
        ],
    }),
    columnHelper.group({
        header: i18next.t("pages:devicePage.table.groupHeader.system"),
        columns: [
            columnHelper.accessor("systemVersion", {
                id: "systemVersion",
                header: i18next.t("pages:devicePage.table.header.version"),
                sortingFn: (a, b) => SemverService.compare(a.original.systemVersion, b.original.systemVersion),
                meta: { filterVariant: "systemVersion" },
                filterFn: columnFilter,
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("systemUptime", {
                id: "systemUptime",
                header: i18next.t("pages:devicePage.table.header.uptime"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("systemTime", {
                id: "systemTime",
                header: i18next.t("pages:devicePage.table.header.time"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("systemTimezone", {
                id: "systemTimezone",
                header: i18next.t("pages:devicePage.table.header.timezone"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
        ],
    }),
    columnHelper.group({
        header: i18next.t("pages:devicePage.table.groupHeader.connection"),
        columns: [
            columnHelper.accessor("connection", {
                id: "connection",
                header: i18next.t("pages:devicePage.table.header.type"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("ipv4", {
                id: "ipv4",
                header: i18next.t("pages:devicePage.table.header.lastIpv4"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("ipv6", {
                id: "ipv6",
                header: i18next.t("pages:devicePage.table.header.lastIpv6"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("wifiNetwork", {
                id: "wifiNetwork",
                header: i18next.t("pages:devicePage.table.header.ssid"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("onlineSince", {
                id: "onlineSince",
                header: i18next.t("pages:devicePage.table.header.onlineSince"),
                cell: (info) => <TableCell value={info.getValue()} />,
            }),
            columnHelper.accessor("lastActivityDate", {
                id: "lastActivityDate",
                header: i18next.t("pages:devicePage.table.header.lastActivity"),
                cell: (info) => <LastActivityCell value={info.getValue()} />,
                sortingFn: (a, b) => {
                    const dateA = a.original.lastActivityDate ? new Date(a.original.lastActivityDate).getTime() : 0;
                    const dateB = b.original.lastActivityDate ? new Date(b.original.lastActivityDate).getTime() : 0;
                    return dateB - dateA;
                },
            }),
        ],
    }),
];
